import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import PasswordStrengthMeter from '../components/PasswordStrengthMeter';

const UpdatePasswordPage = () => {
  const { updatePassword } = useAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      const { error } = await updatePassword(password);
      if (error) throw error;
      navigate('/story');
    } catch (err) {
      setError(err.message || 'An error occurred updating your password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100dvh-8rem)] flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full space-y-8 bg-white dark:bg-purple-900/20 p-10 rounded-3xl border border-gray-100 dark:border-purple-800 shadow-2xl"
      >
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900 dark:text-white">
            Set your password
          </h2>
          <p className="mt-2 text-center text-sm text-gray-900 dark:text-purple-400">
            Please set a password for your account to continue.
          </p>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit} data-testid="update-password-form">
          {error && (
            <div className="p-3 bg-red-100 text-red-700 rounded-lg text-sm text-center">
              {error}
            </div>
          )}
          <div className="rounded-md shadow-sm space-y-4">
            <div className="relative">
              <label className="sr-only">New Password</label>
              <input
                type={showPassword ? "text" : "password"}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-field w-full pr-10"
                placeholder="New Password"
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-900 hover:text-amber-500 transition-colors dark:text-purple-400"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex="-1"
              >
                <SafeIcon icon={showPassword ? FiIcons.FiEyeOff : FiIcons.FiEye} />
              </button>
            </div>

            <PasswordStrengthMeter password={password} />

            <div className="relative">
              <label className="sr-only">Confirm Password</label>
              <input
                type={showPassword ? "text" : "password"}
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="input-field w-full pr-10"
                placeholder="Confirm Password"
              />
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={loading}
              className="w-full btn-primary py-3 flex justify-center items-center space-x-2"
            >
              {loading ? (
                <SafeIcon icon={FiIcons.FiLoader} className="animate-spin" />
              ) : (
                <>
                  <span>Set Password</span>
                  <SafeIcon icon={FiIcons.FiCheck} />
                </>
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default UpdatePasswordPage;
